import React, { useState, useEffect } from "react";
import {
  Search, Plus, Download, Filter,
  ShoppingCart, List, LayoutGrid,
  ArrowUpAZ, ArrowDownZA,
  ChevronRight,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { getSalesList } from "@/redux/Sales/actions";
import FileUpload from "../FileUpload/FileUpload";
import { callLoader } from "@/redux/Loader/actions";
import { useDebounced } from "@/hooks/useDebounced";
import OrderCard, { SALES_ORDER_MAP } from "../orderCard/OrderCard";
import { useNavigate } from "react-router-dom";

// ─── Animated typewriter title ─────────────────────────────────────────────────
const TITLE = "Sales Order Management";
function AnimatedTitle() {
  return (
    <motion.h1
      initial={{ opacity: 0, y: 8, filter: "blur(6px)" }}
      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      className="text-[18px] sm:text-[20px] font-bold tracking-tight leading-tight
                 text-gray-900 dark:text-white"
    >
      <motion.span
        className="inline-block overflow-hidden whitespace-nowrap align-bottom"
        initial={{ width: "0ch" }}
        animate={{
          width: ["0ch", `${TITLE.length}ch`, `${TITLE.length}ch`, "0ch"],
        }}
        transition={{
          duration: 4.8,
          times: [0, 0.22, 0.9, 1],
          ease: ["linear", "linear", "easeInOut"],
          repeat: Infinity,
        }}
      >
        {TITLE}
      </motion.span>
    </motion.h1>
  );
}

// ─── View Toggle ───────────────────────────────────────────────────────────────
function ViewToggle({ view, setView }) {
  const base =
    "w-9 h-9 flex items-center justify-center rounded-md transition-colors";
  const active =
    "bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400";
  const inactive =
    "text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100";

  return (
    <div
      className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700
                  bg-white dark:bg-gray-900 p-0.5"
    >
      <button
        onClick={() => setView("table")}
        title="List view"
        className={`${base} ${view === "table" ? active : inactive}`}
      >
        <List className="w-4 h-4" />
      </button>
      <button
        onClick={() => setView("card")}
        title="Grid view"
        className={`${base} ${view === "card" ? active : inactive}`}
      >
        <LayoutGrid className="w-4 h-4" />
      </button>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
const SalesOrderList = ({ onCreateNew, onEditOrder }) => {
  const navigate = useNavigate();

  // ── State ──────────────────────────────────────────────────────────────────
  const [searchText, setSearchText] = useState("");
  const [searchField, setSearchField] = useState("ALL");
  const [fileUploadVisible, setFileUploadVisible] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [noteModalVisible, setNoteModalVisible] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [view, setView] = useState("table");
  const [sortBy, setSortBy] = useState("recent");
  const [sortDirection, setSortDirection] = useState("desc");

  const { salesList, pagination } = useSelector((state) => state.sales);
  const dispatch = useDispatch();
  const [authUser] = JSON.parse(localStorage.getItem("authUser") ?? "[{}]");
  const companyId = authUser?.companyId;

  // ── Fetch (all original logic unchanged) ──────────────────────────────────
  const debouncedSearch = useDebounced((value) => {
    _getSalesList(1, value);
  }, 300);

  useEffect(() => {
    if (searchText === null) return;
    debouncedSearch(searchText);
  }, [searchText]);

  useEffect(() => {
    _getSalesList(1);
  }, [pageSize]);

  const _getSalesList = async (page = 1) => {
    const payload = {
      IPageNo: page,
      IPageRecords: pageSize,
      FilterType: searchField,
      SearchKeyword: searchText,
      compnayId: companyId || 0,
    };
    dispatch(callLoader(1));
    try {
      await dispatch(getSalesList(payload));
      setCurrentPage(page);
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(callLoader(0));
    }
  };

  const submitNote = async () => {
    if (!noteText) {
      alert("Note cannot be empty!");
      return;
    }
    try {
      const response = await fetch(
        `/api/orders/${selectedOrderId?.soId}/notes`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ note: noteText }),
        }
      );
      if (response.ok) {
        alert("Note added successfully!");
        setNoteModalVisible(false);
        setNoteText("");
      } else {
        alert("Failed to add note!");
      }
    } catch (error) {
      console.error("Error submitting note:", error);
    }
  };

  const changePage = (page) => _getSalesList(page);

  const statusOptions = [
    { value: "ALL", label: "All" },
    { value: "ACTIVE", label: "Active" },
    { value: "CLOSED", label: "Closed" },
  ];

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-full space-y-3 p-2 sm:p-3 lg:p-4">

      {/* ════ HEADER CARD ════════════════════════════════════════════════════ */}
      <div
        className="rounded-2xl overflow-hidden
                    border border-gray-200 dark:border-gray-800
                    bg-white dark:bg-gray-900
                    shadow-sm dark:shadow-gray-900/40"
      >
        {/* Top bar */}
        <div
          className="px-4 sm:px-5 py-4
                      flex flex-wrap items-center justify-between gap-3
                      border-b border-gray-100 dark:border-gray-800"
        >
          {/* Left — icon + title */}
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-xl
                          bg-blue-50 dark:bg-blue-900/40
                          ring-1 ring-blue-100 dark:ring-blue-800
                          flex items-center justify-center"
            >
              <ShoppingCart
                className="w-5 h-5 text-blue-600 dark:text-blue-400"
                strokeWidth={2.2}
              />
            </div>
            <div className="min-w-0">
              <AnimatedTitle />
              <p className="mt-0.5 text-[12px] text-gray-500 dark:text-gray-400 hidden sm:block truncate max-w-sm lg:max-w-lg">
                Manage and track all your sales orders in one place
              </p>
            </div>
          </div>

          {/* Right — action buttons + view toggle */}
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <button
              className="inline-flex items-center gap-1.5 h-9 px-3 sm:px-3.5 rounded-lg
                           bg-white dark:bg-gray-800
                           border border-gray-200 dark:border-gray-700
                           text-gray-700 dark:text-gray-300
                           hover:bg-gray-50 hover:border-gray-300
                           dark:hover:bg-gray-700/70 dark:hover:border-gray-600
                           text-[12.5px] font-semibold transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              <span className="hidden xs:inline">Export</span>
            </button>

            <button
              onClick={onCreateNew}
              className="inline-flex items-center gap-1.5 h-9 px-3 sm:px-3.5 rounded-lg
                           bg-blue-600 hover:bg-blue-700 active:bg-blue-800
                           dark:bg-blue-500 dark:hover:bg-blue-600
                           text-white text-[12.5px] font-semibold
                           shadow-[0_4px_14px_-4px_rgba(37,99,235,0.45)]
                           dark:shadow-[0_4px_14px_-4px_rgba(59,130,246,0.35)]
                           transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              <span className="hidden xs:inline">New Order</span>
              <span className="xs:hidden">Order</span>
            </button>

            <ViewToggle view={view} setView={setView} />
          </div>
        </div>
      </div>
      {/* ════ END HEADER CARD ════ */}

      {/* ── Filter Bar ──────────────────────────────────────────────────────── */}
      <div
        className="rounded-xl border border-gray-200 dark:border-gray-800
                    bg-white dark:bg-gray-900 shadow-sm px-4 sm:px-5 py-4 space-y-4"
      >
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative flex items-center gap-2">
            {/* Field selector */}
            <select
              value={searchField}
              onChange={(e) => setSearchField(e.target.value)}
              className="px-3 py-2.5 bg-gray-50 dark:bg-gray-800
                         border border-gray-200 dark:border-gray-700
                         rounded-xl text-[12.5px] text-gray-700 dark:text-gray-300
                         focus:outline-none focus:ring-2 focus:ring-blue-500 shrink-0"
            >
              <option value="ALL">All Fields</option>
              <option value="SON">SalesOrder No.</option>
              <option value="WARE">Warehouse</option>
              <option value="POL">POL</option>
              <option value="POD">POD</option>
              <option value="SKU">SKU</option>
              <option value="CUST">Customer</option>
            </select>

            {/* Search input */}
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by order number, customer, or destination..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5
                           bg-gray-50 dark:bg-gray-800
                           border border-gray-200 dark:border-gray-700
                           rounded-xl text-sm text-gray-900 dark:text-white
                           placeholder-gray-400
                           focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                           transition-all"
              />
            </div>
          </div>

          {/* Controls */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Status tabs */}
            <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-xl p-1">
              {statusOptions.map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => setSearchField(value)}
                  className={`px-3 py-1.5 rounded-lg text-[12.5px] font-medium transition-all ${
                    searchField === value
                      ? "bg-blue-600 text-white shadow-sm"
                      : "text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Sort direction + column */}
            <div className="flex items-center gap-1.5">
              <button
                onClick={() =>
                  setSortDirection((d) => (d === "asc" ? "desc" : "asc"))
                }
                className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                {sortDirection === "asc" ? (
                  <ArrowUpAZ className="w-4 h-4 text-gray-500" />
                ) : (
                  <ArrowDownZA className="w-4 h-4 text-gray-500" />
                )}
              </button>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700
                           rounded-xl text-[12.5px] text-gray-700 dark:text-gray-300
                           focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="recent">Most Recent</option>
                <option value="customer">Customer Name</option>
                <option value="amount">Order Amount</option>
              </select>
            </div>
          </div>
        </div>

        {/* Results summary */}
        <div className="pt-3 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
          <p className="text-[12.5px] text-gray-500 dark:text-gray-400">
            Showing{" "}
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {pagination?.currentRecord || 0}
            </span>{" "}
            of{" "}
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {pagination?.totalRecord || 0}
            </span>{" "}
            orders
          </p>
        </div>
      </div>

      {/* ── Content ─────────────────────────────────────────────────────────── */}
      <div className="p-1">
        {/* Empty state */}
        {(salesList || []).length === 0 ? (
          <div className="text-center py-20 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800">
            <ShoppingCart className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-300 font-semibold text-lg">
              No orders found
            </p>
            <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">
              Try adjusting your search or filter criteria
            </p>
            <button
              onClick={onCreateNew}
              className="mt-4 px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors"
            >
              + New Order
            </button>
          </div>
        ) : view === "card" ? (
          /* Card grid */
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(salesList || []).map((order) => (
              <OrderCard
                key={order.soId}
                record={order}
                fieldMap={SALES_ORDER_MAP}
         
                navigate={navigate}
                t={(k) => k}
                onOpenFileUpload={() => {
                  setSelectedOrderId(order);
                  setFileUploadVisible(true);
                }}
                onOpenNotes={() => {
                  setSelectedOrderId(order);
                  setNoteModalVisible(true);
                }}
                onEdit={onEditOrder}
                onDelete={() => {}}
                onRestore={() => {}}
              />
            ))}
          </div>
        ) : (
          /* List view */
          <div className="space-y-4">
            <AnimatePresence>
              {(salesList || []).map((order) => (
                <OrderCard
                  key={order.soId}
                  record={order}
                  fieldMap={SALES_ORDER_MAP}
               
                  navigate={navigate}
                  t={(k) => k}
                  onOpenFileUpload={() => {
                    setSelectedOrderId(order);
                    setFileUploadVisible(true);
                  }}
                  onOpenNotes={() => {
                    setSelectedOrderId(order);
                    setNoteModalVisible(true);
                  }}
                  onEdit={onEditOrder}
                  onDelete={() => {}}
                  onRestore={() => {}}
                />
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* Pagination */}
        {(salesList || []).length > 0 && (
          <div
            className="border-t border-gray-200 dark:border-gray-800
                        pl-2 pr-16 pt-4 pb-8 mb-4 mt-8
                        bg-gray-50 dark:bg-gray-800/50
                        flex items-center justify-between rounded-b-xl"
          >
            {/* Per-page selector */}
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <span>Show</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700
                           rounded-lg text-sm text-gray-700 dark:text-gray-300
                           focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {[5, 10, 25, 50].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <span>entries</span>
            </div>

            {/* Page controls */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => currentPage > 1 && changePage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-700 rounded-lg
                           hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40 transition-colors"
              >
                Previous
              </button>
              <button className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg cursor-default">
                {currentPage}
              </button>
              <button
                onClick={() =>
                  pagination?.nextPage > 0 && changePage(pagination.nextPage)
                }
                disabled={!pagination?.nextPage || pagination.nextPage === 0}
                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-700 rounded-lg
                           hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40 transition-colors"
              >
                Next
              </button>
            </div>

            <p className="text-sm text-gray-500 dark:text-gray-400">
              Showing{" "}
              <span className="font-medium">
                {pagination?.currentRecord || 0}
              </span>{" "}
              of{" "}
              <span className="font-medium">{pagination?.totalRecord || 0}</span>{" "}
              orders
            </p>
          </div>
        )}
      </div>

      {/* ── Note modal (original logic unchanged) ──────────────────────────── */}
      {noteModalVisible && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 w-[70%] p-6 rounded-xl shadow-xl">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
              Add Note
            </h2>
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows={5}
              className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-lg mb-4
                         bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white"
              placeholder="Write your note here..."
            />
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setNoteModalVisible(false)}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg text-sm font-medium transition-colors"
              >
                Close
              </button>
              <button
                onClick={submitNote}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── File upload modal (original logic unchanged) ────────────────────── */}
      {fileUploadVisible && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 w-[70%] p-6 rounded-xl shadow-xl">
            <FileUpload
              orderId={selectedOrderId?.soId}
              orderNum={selectedOrderId?.soNumber}
              type="sales"
            />
            <div className="flex justify-end mt-6">
              <button
                onClick={() => {
                  setFileUploadVisible(false);
                  setSelectedOrderId(null);
                }}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg text-sm font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesOrderList;